import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Tag } from './models';
import { baseApi } from '@centerpiece/components';

export const tag = baseApi
  .enhanceEndpoints({ addTagTypes: ['Tags', 'Tag'] })
  .injectEndpoints({
    endpoints: builder => ({
      getTags: builder.query<Tag[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'tag',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Tag' as const, id })),
                'Tags',
              ]
            : ['Tags'],
      }),
      getTag: builder.query<Tag, string>({
        query: id => ({
          url: `tag/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [{ type: 'Tag', id: arg }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createTag: builder.mutation({
        query: request => ({
          url: 'tag',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Tags'],
      }),
      updateTag: builder.mutation<Tag, Tag>({
        query: request => ({
          url: `tag/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [{ type: 'Tag', id: arg.id }],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            tag.util.updateQueryData('getTag', `${id}`, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(tag.util.invalidateTags(['Tags']))
             */
            dispatch(tag.util.invalidateTags(['Tags']));
          }
        },
      }),
      deleteTag: builder.mutation<Tag, number>({
        query: id => ({
          url: `tag/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result ? [{ type: 'Tag' as const, arg }, 'Tags'] : ['Tags'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetTagsQuery,
  useGetTagQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  usePrefetch: useTagsPrefetch,
  useLazyGetTagsQuery,
} = tag;
const selectTagsRoot = tag.endpoints.getTags.select({});
export const selectTags = createSelector(selectTagsRoot, tag => tag.data || []);
